
/**
 * Adds resource hints to improve loading performance
 * 
 * @param resources List of resources to add hints for
 */
export function addResourceHints(resources: {
  preconnect?: string[];
  prefetch?: string[];
  preload?: Array<{ href: string; as: 'script' | 'style' | 'image' | 'font' | 'fetch'; type?: string; crossOrigin?: string }>;
}) {
  const { preconnect = [], prefetch = [], preload = [] } = resources;
  
  // Add preconnect hints
  preconnect.forEach(url => {
    if (!document.querySelector(`link[rel="preconnect"][href="${url}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = url;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }
  });
  
  // Add prefetch hints for future navigations
  prefetch.forEach(url => {
    if (!document.querySelector(`link[rel="prefetch"][href="${url}"]`)) {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      document.head.appendChild(link);
    }
  });
  
  // Add preload hints for critical resources
  preload.forEach(resource => {
    if (!document.querySelector(`link[rel="preload"][href="${resource.href}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = resource.href;
      link.as = resource.as;
      if (resource.type) link.type = resource.type;
      if (resource.crossOrigin) link.crossOrigin = resource.crossOrigin;
      document.head.appendChild(link);
    }
  });
}

/**
 * Prioritizes critical data fetching
 */
export function scheduleDataFetching(
  criticalFetches: Array<() => Promise<any>>,
  nonCriticalFetches: Array<() => Promise<any>>
) {
  // Execute critical fetches immediately
  const criticalPromises = criticalFetches.map(fetch => fetch());
  
  // Schedule non-critical fetches for later
  const executeNonCritical = () => {
    // Use requestIdleCallback if available, or setTimeout as fallback
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => {
        nonCriticalFetches.forEach(fetch => fetch());
      });
    } else {
      setTimeout(() => {
        nonCriticalFetches.forEach(fetch => fetch());
      }, 200);
    }
  };
  
  // After critical fetches complete or after a timeout, run non-critical fetches
  Promise.race([
    Promise.all(criticalPromises),
    new Promise(resolve => setTimeout(resolve, 1500))
  ]).then(executeNonCritical);
}

/**
 * Adds critical CSS inline to improve First Contentful Paint
 * @param cssText CSS content to inline
 */
export function addInlineCriticalCSS(cssText: string) {
  if (typeof document === 'undefined') return;
  
  // Check if we already have critical CSS added
  if (document.querySelector('style[data-critical="true"]')) return;
  
  // Create and append style element with critical CSS
  const style = document.createElement('style');
  style.setAttribute('data-critical', 'true');
  style.textContent = cssText;
  document.head.appendChild(style);
}

/**
 * Critical CSS for the initial page load
 * This includes minimal styles needed for the first paint
 */
export const criticalCSS = `
.bg-gray-50 { background-color: #F9FAFB; }
.container { width: 100%; padding-left: 1rem; padding-right: 1rem; margin-left: auto; margin-right: auto; }
@media (min-width: 640px) { .container { max-width: 640px; } }
@media (min-width: 768px) { .container { max-width: 768px; } }
@media (min-width: 1024px) { .container { max-width: 1024px; } }
@media (min-width: 1280px) { .container { max-width: 1280px; } }
@media (min-width: 1400px) { .container { max-width: 1400px; } }
.flex { display: flex; }
.flex-col { flex-direction: column; }
.min-h-screen { min-height: 100vh; }
.text-center { text-align: center; }
.text-gray-500 { color: #6B7280; }
.text-xl { font-size: 1.25rem; line-height: 1.75rem; }
.font-semibold { font-weight: 600; }
.mb-2 { margin-bottom: 0.5rem; }
.p-4 { padding: 1rem; }
.items-center { align-items: center; }
.justify-center { justify-content: center; }
`;

/**
 * Creates a progressive bundle loader
 * Loads JS bundles in order of importance
 */
export function loadBundles(bundles: Array<{src: string, priority: 'high'|'medium'|'low'}>) {
  // High priority bundles
  const highPriority = bundles.filter(b => b.priority === 'high');
  // Medium and low priority bundles
  const lowerPriority = bundles.filter(b => b.priority !== 'high');
  
  // Function to load a script
  const loadScript = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.body.appendChild(script);
    });
  };
  
  // Load high priority bundles immediately
  Promise.all(highPriority.map(b => loadScript(b.src)))
    .then(() => {
      // Load medium and low priority bundles when idle or after a timeout
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(() => {
          lowerPriority.forEach(b => loadScript(b.src));
        });
      } else {
        setTimeout(() => {
          lowerPriority.forEach(b => loadScript(b.src));
        }, 2000); // Wait for 2 seconds after critical bundles load
      }
    })
    .catch(error => {
      console.error('Error loading bundles:', error);
    });
}
